import { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material'
import {
  TableChart as ExcelIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material'
import ReportsTabs from '../../components/reports/ReportsTabs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, subDays } from 'date-fns'
import {
  useAdvanceTaxReport,
  useGenerateExcelReport,
} from '../../hooks/api/useReports'
import { formatCurrencyWithLocale as formatCurrency } from '../../utils/formatters'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Navigate } from 'react-router-dom'

const ReportsPage = () => {
  const user = useSelector((state: RootState) => state.auth.user)
  const advanceTaxEnabled = user?.company_features?.advance_tax !== false
  const salesTaxEnabled = user?.company_features?.sales_tax_invoice === true
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 30))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [viewReport, setViewReport] = useState(false)

  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : ''
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : ''

  const { data: reportData, isLoading: isLoadingReport } = useAdvanceTaxReport(
    viewReport && formattedStartDate && formattedEndDate
      ? { startDate: formattedStartDate, endDate: formattedEndDate, format: 'json' }
      : undefined
  )

  const generateExcelMutation = useGenerateExcelReport()

  const handleViewReport = () => {
    if (startDate && endDate) {
      setViewReport(true)
    }
  }

  const handleGenerateExcel = () => {
    if (formattedStartDate && formattedEndDate) {
      generateExcelMutation.mutate({ startDate: formattedStartDate, endDate: formattedEndDate })
    }
  }

  const isGenerating = generateExcelMutation.isPending

  if (!advanceTaxEnabled) return <Navigate to={salesTaxEnabled ? '/reports/sales-tax' : '/reports/pnl'} replace />

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Reports
        </Typography>

        <ReportsTabs activeTab={0} />

        {advanceTaxEnabled ? <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Advance Tax Report
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Generate advance tax reports for filers (0.5%) and non-filers (2.5%)
          </Typography>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue)
                  setViewReport(false)
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue)
                  setViewReport(false)
                }}
                minDate={startDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={handleViewReport}
                  disabled={!startDate || !endDate || isLoadingReport}
                  startIcon={isLoadingReport ? <CircularProgress size={20} /> : <ReportIcon />}
                >
                  View Report
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleGenerateExcel}
                  disabled={!startDate || !endDate || isGenerating}
                  startIcon={isGenerating ? <CircularProgress size={20} /> : <ExcelIcon />}
                >
                  Generate Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper> : <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6">Advance tax is disabled</Typography>
          <Typography variant="body2" color="text.secondary">This company does not apply advance tax to invoices.</Typography>
        </Paper>}

        {/* Report Summary */}
        {viewReport && reportData && (
          <Box>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Customers
                    </Typography>
                    <Typography variant="h4">
                      {reportData.summary.total_customers.toLocaleString()}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <StatusChip label={`Filers: ${reportData.summary.filer_customers.toLocaleString()}`} variant="success" />
                      <StatusChip label={`Non-Filers: ${reportData.summary.non_filer_customers.toLocaleString()}`} variant="warn" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Bills
                    </Typography>
                    <Typography variant="h4">
                      {reportData.summary.total_bills.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Taxable Amount
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(reportData.summary.total_taxable_amount)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Tax Amount
                    </Typography>
                    <Typography variant="h5" color="error">
                      {formatCurrency(reportData.summary.total_tax_amount)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Filer vs Non-Filer Breakdown */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom color="success.main">
                    Filers (0.5% Tax Rate)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Customers:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {reportData.summary.filer_customers.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Taxable Amount:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(reportData.summary.filer_taxable_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Tax Amount:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {formatCurrency(reportData.summary.filer_tax_amount)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom color="warning.main">
                    Non-Filers (2.5% Tax Rate)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Customers:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {reportData.summary.non_filer_customers.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Taxable Amount:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(reportData.summary.non_filer_taxable_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Tax Amount:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {formatCurrency(reportData.summary.non_filer_tax_amount)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {isLoadingReport && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading report data...</Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default ReportsPage
